import React, { useState, useMemo } from 'react';
import {
    Box,
    Text,
    Button,
    Title,
    Group,
    useMantineColorScheme
} from '@mantine/core';
import {
    compareObjects,
    normalizeDataForComparison
} from '../../utils/patchOperationUtils';
// react-syntax-highlighter: JSON Before/After diff panels with line-based highlight (add/remove/change).
// We use HLJS + monokai (dark) / github (light) so diff is readable in both modes; not used for inline text.
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import { monokai, github } from 'react-syntax-highlighter/dist/esm/styles/hljs';

interface JsonDiffViewerProps {
    originalData: any;
    currentData: any;
    title?: string;
    showLegend?: boolean;
    collapsible?: boolean;
    defaultExpanded?: boolean;
    excludedPaths?: string[]; // Paths to exclude from comparison (e.g., 'Id', 'EntityType')
}

// Re-export compareObjects for backward compatibility
export { compareObjects };

/**
 * Recursively remove keys that start with _ or $ (metadata / debug) so diff and display
 * show only entity data. Callers do not need to strip these before passing data.
 */
function stripMetaKeys(data: any): any {
    if (data == null || typeof data !== 'object') {
        return data;
    }
    if (Array.isArray(data)) {
        return data.map((item) => stripMetaKeys(item));
    }
    const out: Record<string, any> = {};
    for (const [key, value] of Object.entries(data)) {
        if (key.startsWith('_') || key.startsWith('$')) continue;
        out[key] = stripMetaKeys(value);
    }
    return out;
}

/**
 * Utility function to highlight JSON with differences (for backward compatibility)
 * @deprecated This function is kept for backward compatibility but is no longer used internally
 */
export const highlightJsonWithDifferences = (
    jsonString: string,
    differences: { [key: string]: 'added' | 'removed' | 'changed' },
    isAfter: boolean,
    themeOrColorScheme: { palette?: { mode?: string } } | 'light' | 'dark'
): Array<{ line: string; backgroundColor: string }> => {
    const isDark = typeof themeOrColorScheme === 'string'
        ? themeOrColorScheme === 'dark'
        : themeOrColorScheme?.palette?.mode === 'dark';

    const lines = jsonString.split('\n');
    const highlightedLines = lines.map((line) => {
        // Find if this line contains a changed property
        let hasChange = false;
        let changeType: 'added' | 'removed' | 'changed' | null = null;

        for (const [path, type] of Object.entries(differences)) {
            // Check if this line contains the property
            const propertyName = path.split('.').pop();
            if (propertyName && line.includes(`"${propertyName}"`)) {
                hasChange = true;
                changeType = type;
                break;
            }
        }

        if (!hasChange) {
            return { line, backgroundColor: 'transparent' };
        }

        // Determine background color based on change type and which JSON we're highlighting
        let backgroundColor = 'transparent';
        if (changeType === 'added') {
            backgroundColor = isAfter
                ? isDark ? '#1b5e20' : '#e8f5e8'
                : 'transparent'; // Green for additions in after
        } else if (changeType === 'removed') {
            backgroundColor = isAfter
                ? 'transparent'
                : isDark ? '#b71c1c' : '#ffeaea'; // Red for removals in before
        } else if (changeType === 'changed') {
            backgroundColor = isDark ? '#f57f17' : '#fff3cd'; // Yellow for changes in both
        }

        return { line, backgroundColor };
    });

    return highlightedLines;
};

/**
 * Reusable component for displaying JSON differences between original and current data
 */
export const JsonDiffViewer: React.FC<JsonDiffViewerProps> = ({
    originalData,
    currentData,
    title = 'JSON Comparison',
    showLegend = true,
    collapsible = true,
    defaultExpanded = false,
    excludedPaths = ['Id', 'EntityType'] // Default excluded paths
}) => {
    const { colorScheme } = useMantineColorScheme();
    const [showJsonView, setShowJsonView] = useState(defaultExpanded);
    const isDark = colorScheme === 'dark';

    // Helper to normalize Body strings for comparison
    const normalizeBodyString = (value: string): string => {
        if (typeof value !== 'string') return value;

        // Check if it's a JSON-escaped string
        const trimmed = value.trim();
        if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 2) {
            try {
                const parsed = JSON.parse(value);
                if (typeof parsed === 'string') {
                    // Only normalize line endings, preserve all other content including spaces
                    return parsed.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
                }
            } catch {
                // Not a JSON-escaped string, normalize line endings only
                return value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
            }
        }
        // Normalize line endings only, preserve content
        return value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    };

    // Normalize data for diff comparison - exclude Config metadata and normalize Body
    const normalizeForDiffComparison = (data: any): any => {
        return normalizeDataForComparison(data, {
            excludeConfigMetadata: true, // Exclude editVersion and editDate from Config/Settings
            normalizeBody: normalizeBodyString // Normalize Body line endings
        });
    };

    // Custom stringify that formats Body property as multiline HTML
    const customStringify = (data: any, indent: number = 2): string => {
        let jsonString = JSON.stringify(data, null, indent);

        // Post-process the JSON string to format Body as multiline
        // We need to find "Body": "..." and replace the escaped string with multiline format
        // The regex must handle all JSON escape sequences: \\, \", \n, \t, \r, \uXXXX, etc.
        const bodyRegex = /("(?:Body|body)"\s*:\s*)"((?:[^"\\]|\\(?:["\\/bfnrt]|u[0-9a-fA-F]{4}))*)"/g;

        return jsonString.replace(bodyRegex, (match, prefix, escapedValue) => {
            // Unescape the JSON-escaped string to get the actual HTML content
            let unescapedValue: string;
            try {
                // Parse the escaped JSON string to get the actual value
                unescapedValue = JSON.parse(`"${escapedValue}"`);
            } catch {
                // If parsing fails, return the match as-is
                return match;
            }

            // Check if we should format as multiline
            // Format if: contains HTML tags, has actual newlines, or is very long
            const shouldFormat = unescapedValue.includes('<') ||
                unescapedValue.includes('\n') ||
                unescapedValue.length > 100;

            if (shouldFormat) {
                // Split into lines
                const lines = unescapedValue.split('\n');
                const nestedIndent = ' '.repeat(indent * 2); // Extra indent for continuation lines

                if (lines.length > 1) {
                    // Multiline - format each line with proper indentation
                    const formattedLines: string[] = [];

                    lines.forEach((line, index) => {
                        // Escape special characters for JSON (but keep newlines as actual newlines)
                        // We need to escape backslashes and quotes, but NOT newlines
                        const escapedLine = line
                            .replace(/\\/g, '\\\\')  // Escape backslashes
                            .replace(/"/g, '\\"');   // Escape quotes

                        if (index === 0) {
                            // First line: "Body": "first line
                            formattedLines.push(`${prefix}"${escapedLine}`);
                        } else if (index === lines.length - 1) {
                            // Last line:     last line"
                            formattedLines.push(`${nestedIndent}${escapedLine}"`);
                        } else {
                            // Middle lines:     middle line
                            formattedLines.push(`${nestedIndent}${escapedLine}`);
                        }
                    });

                    return formattedLines.join('\n');
                } else if (unescapedValue.length > 100) {
                    // Single line but very long - we could wrap it, but for now keep as single line
                    // The syntax highlighter should handle long lines
                    return match;
                }
            }

            // Not multiline or doesn't need formatting, return as-is
            return match;
        });
    };

    // Sort top-level keys for display, keeping Config/Settings last.
    // This is display-only (does not affect diff computation).
    const sortTopLevelForDisplay = (data: any): any => {
        if (!data || typeof data !== 'object' || Array.isArray(data)) return data;

        const entries = Object.entries(data);
        const isConfigLikeKey = (k: string) => k === 'Config' || k === 'config';
        const isSettingsLikeKey = (k: string) => k === 'Settings' || k === 'settings';

        const normal = entries
            .filter(([k]) => !isConfigLikeKey(k) && !isSettingsLikeKey(k))
            .sort(([a], [b]) => a.localeCompare(b));

        const configLike = entries.filter(([k]) => isConfigLikeKey(k));
        const settingsLike = entries.filter(([k]) => isSettingsLikeKey(k));

        return Object.fromEntries([...normal, ...configLike, ...settingsLike]);
    };

    // Compute differences and JSON strings
    const { differences, beforeJson, afterJson } = useMemo(() => {
        // Strip _ and $ keys so diff and live preview show only entity data (no metadata/debug)
        const cleanedOriginal = stripMetaKeys(originalData);
        const cleanedCurrent = stripMetaKeys(currentData);

        // Normalize data for comparison - this handles Body unescaping and Config metadata removal
        const normalizedOriginal = normalizeForDiffComparison(cleanedOriginal);
        const normalizedCurrent = normalizeForDiffComparison(cleanedCurrent);

        // Use compareObjects with preserveFalseValues=true for diff display
        // This ensures false values are preserved so false->true changes are detected as 'changed'
        const diffs = compareObjects(normalizedOriginal, normalizedCurrent, '', {
            excludedPaths: excludedPaths,
            preserveFalseValues: true // Preserve false values for diff display
        });

        // Config → Settings migration: show as one logical change (yellow on both sides) instead of red delete + green add
        if (diffs['Config'] === 'removed' && diffs['Settings'] === 'added') {
            diffs['Config'] = 'changed';
            diffs['Settings'] = 'changed';
        }

        // Use cleaned data for display so Before/After panels do not show _ or $ attributes
        const before = customStringify(sortTopLevelForDisplay(cleanedOriginal), 2);
        const after = customStringify(sortTopLevelForDisplay(cleanedCurrent), 2);

        return {
            differences: diffs,
            beforeJson: before,
            afterJson: after
        };
    }, [originalData, currentData, excludedPaths]);

    // Helper to determine line highlighting based on differences
    const getLineProps = (lineNumber: number, isAfter: boolean) => {
        const lineIndex = lineNumber - 1; // Convert to 0-based index
        const lines = isAfter ? afterJson.split('\n') : beforeJson.split('\n');
        if (lineIndex < 0 || lineIndex >= lines.length) {
            return { style: {} };
        }

        const line = lines[lineIndex];

        // Check if this line contains any changed properties
        for (const [path, type] of Object.entries(differences)) {
            const propertyName = path.split('.').pop() || '';

            if (!propertyName) continue;

            // Escape special regex characters in property name
            const escapedName = propertyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

            // Match patterns like: "DisplayName": or "DisplayName": (with optional whitespace before colon)
            // The pattern should match the property name followed by optional whitespace and a colon
            // Use case-insensitive match to handle any case variations
            const propertyPattern = new RegExp(`"${escapedName}"\\s*:`, 'i');

            // Test if this line matches the property pattern
            // The pattern ensures we match the property declaration line (with colon)
            if (propertyPattern.test(line)) {
                let shouldHighlight = false;
                let backgroundColor = '';

                if (type === 'added') {
                    shouldHighlight = isAfter;
                    backgroundColor = isDark ? '#2e7d32' : '#e8f5e8'; // Green for dark mode
                } else if (type === 'removed') {
                    shouldHighlight = !isAfter;
                    backgroundColor = isDark ? '#c62828' : '#ffeaea'; // Red for dark mode
                } else if (type === 'changed') {
                    shouldHighlight = true;
                    // Use a darker, more muted orange/brown that won't conflict with syntax highlighting
                    backgroundColor = isDark ? '#8b6914' : '#fff3cd'; // Darker orange-brown for dark mode
                }

                if (shouldHighlight) {
                    return { style: { backgroundColor } };
                }
            }

            // Special handling for multiline Body property
            // If Body is changed and formatted as multiline, highlight all lines that are part of the Body value
            if ((propertyName === 'Body' || propertyName === 'body') && (type === 'changed' || type === 'added' || type === 'removed')) {
                // Find the "Body": line by looking backwards
                let bodyStartIndex = -1;
                for (let i = lineIndex; i >= 0; i--) {
                    if (propertyPattern.test(lines[i])) {
                        bodyStartIndex = i;
                        break;
                    }
                }

                if (bodyStartIndex >= 0) {
                    // Check if current line is part of the multiline Body value
                    // Lines that are part of Body value will be more indented than the "Body": line
                    const bodyStartLine = lines[bodyStartIndex];
                    const bodyStartIndent = (bodyStartLine.match(/^(\s*)/)?.[1] || '').length;
                    const currentIndent = (line.match(/^(\s*)/)?.[1] || '').length;

                    // If current line is more indented, it's likely part of the Body value
                    // Also check that we're still within the same object (not past the next property)
                    if (currentIndent > bodyStartIndent && lineIndex > bodyStartIndex) {
                        // Check if we've moved to the next property at the same or less indent level
                        let isStillInBody = true;
                        for (let i = bodyStartIndex + 1; i <= lineIndex; i++) {
                            const checkLine = lines[i];
                            const checkIndent = (checkLine.match(/^(\s*)/)?.[1] || '').length;
                            // If we find a line with same or less indent that starts a new property, we've left Body
                            if (checkIndent <= bodyStartIndent && checkLine.trim().startsWith('"') &&
                                !checkLine.includes('"Body"') && !checkLine.includes('"body"') &&
                                checkLine.includes('":')) {
                                if (i < lineIndex) {
                                    isStillInBody = false;
                                }
                                break;
                            }
                        }

                        if (isStillInBody) {
                            let shouldHighlight = false;
                            let backgroundColor = '';

                            if (type === 'added') {
                                shouldHighlight = isAfter;
                                backgroundColor = isDark ? '#2e7d32' : '#e8f5e8';
                            } else if (type === 'removed') {
                                shouldHighlight = !isAfter;
                                backgroundColor = isDark ? '#c62828' : '#ffeaea';
                            } else if (type === 'changed') {
                                shouldHighlight = true;
                                backgroundColor = isDark ? '#8b6914' : '#fff3cd';
                            }

                            if (shouldHighlight) {
                                return { style: { backgroundColor } };
                            }
                        }
                    }
                }
            }

            // General handling for multiline array/object values
            // If a property is changed/added/removed and has multiline values, highlight all lines
            if (type === 'changed' || type === 'added' || type === 'removed') {
                // Find the property declaration line by looking backwards
                let propStartIndex = -1;
                for (let i = lineIndex; i >= 0; i--) {
                    if (propertyPattern.test(lines[i])) {
                        propStartIndex = i;
                        break;
                    }
                }

                if (propStartIndex >= 0) {
                    const propStartLine = lines[propStartIndex];
                    const propStartIndent = (propStartLine.match(/^(\s*)/)?.[1] || '').length;
                    const currentIndent = (line.match(/^(\s*)/)?.[1] || '').length;

                    // Check if current line is part of the property value
                    // It can be:
                    // 1. More indented than property line (value content)
                    // 2. Same indent as property line but is a closing bracket/brace (for arrays/objects)
                    const isClosingBracket = (currentIndent === propStartIndent || currentIndent === propStartIndent - 2) && 
                                             (line.trim() === ']' || line.trim() === '}');
                    const isValueContent = currentIndent > propStartIndent && lineIndex > propStartIndex;
                    
                    if (isValueContent || isClosingBracket) {
                        // Check if we're still within the property value (not past the next property)
                        let isStillInValue = true;
                        for (let i = propStartIndex + 1; i <= lineIndex; i++) {
                            const checkLine = lines[i];
                            const checkIndent = (checkLine.match(/^(\s*)/)?.[1] || '').length;
                            // If we find a line with same or less indent that starts a new property, we've left this property
                            // But allow closing brackets at the same indent level
                            if (checkIndent <= propStartIndent && checkLine.trim().startsWith('"') &&
                                !propertyPattern.test(checkLine) &&
                                checkLine.includes('":')) {
                                if (i < lineIndex) {
                                    isStillInValue = false;
                                }
                                break;
                            }
                        }

                        if (isStillInValue) {
                            let shouldHighlight = false;
                            let backgroundColor = '';
                            let effectiveType = type;

                            // For "changed" properties, check if this specific line is new or removed
                            if (type === 'changed') {
                                // Get the corresponding line from the other JSON
                                const otherLines = isAfter ? beforeJson.split('\n') : afterJson.split('\n');
                                
                                // Find the property in the other JSON
                                let otherPropStartIndex = -1;
                                for (let i = 0; i < otherLines.length; i++) {
                                    if (propertyPattern.test(otherLines[i])) {
                                        otherPropStartIndex = i;
                                        break;
                                    }
                                }

                                if (otherPropStartIndex >= 0) {
                                    // Find the corresponding line in the other JSON by relative position
                                    // Count how many value lines we are from the property start
                                    const valueLineOffset = lineIndex - propStartIndex - 1;
                                    const otherValueLineIndex = otherPropStartIndex + 1 + valueLineOffset;

                                    // Check if the line exists in the other JSON
                                    if (otherValueLineIndex >= 0 && otherValueLineIndex < otherLines.length) {
                                        const otherLine = otherLines[otherValueLineIndex].trim();
                                        const currentLineTrimmed = line.trim();
                                        
                                        // If lines match, it's a change (not add/remove)
                                        if (otherLine === currentLineTrimmed) {
                                            effectiveType = 'changed';
                                        } else {
                                            // Check if this line exists anywhere in the other property value
                                            const otherPropStartIndent = (otherLines[otherPropStartIndex].match(/^(\s*)/)?.[1] || '').length;
                                            let foundInOther = false;
                                            for (let i = otherPropStartIndex + 1; i < otherLines.length; i++) {
                                                const checkOtherLine = otherLines[i];
                                                const checkOtherIndent = (checkOtherLine.match(/^(\s*)/)?.[1] || '').length;
                                                
                                                // Stop if we've left the property value
                                                if (checkOtherIndent <= otherPropStartIndent && checkOtherLine.trim().startsWith('"') &&
                                                    checkOtherLine.includes('":')) {
                                                    break;
                                                }
                                                
                                                // Check if this line matches
                                                if (checkOtherLine.trim() === currentLineTrimmed) {
                                                    foundInOther = true;
                                                    break;
                                                }
                                            }
                                            
                                            if (isAfter && !foundInOther) {
                                                // Line exists in "after" but not in "before" - it's added
                                                effectiveType = 'added';
                                            } else if (!isAfter && !foundInOther) {
                                                // Line exists in "before" but not in "after" - it's removed
                                                effectiveType = 'removed';
                                            } else {
                                                effectiveType = 'changed';
                                            }
                                        }
                                    } else {
                                        // Line doesn't exist at corresponding position - it's new/removed
                                        effectiveType = isAfter ? 'added' : 'removed';
                                    }
                                }
                            }

                            if (effectiveType === 'added') {
                                shouldHighlight = isAfter;
                                backgroundColor = isDark ? '#2e7d32' : '#e8f5e8';
                            } else if (effectiveType === 'removed') {
                                shouldHighlight = !isAfter;
                                backgroundColor = isDark ? '#c62828' : '#ffeaea';
                            } else if (effectiveType === 'changed') {
                                shouldHighlight = true;
                                backgroundColor = isDark ? '#8b6914' : '#fff3cd';
                            }

                            if (shouldHighlight) {
                                return { style: { backgroundColor } };
                            }
                        }
                    }
                }
            }
        }

        return { style: {} };
    };

    if (!originalData || !currentData) {
        return null;
    }

    const borderColor = 'var(--mantine-color-default-border)';

    return (
        <Box pt="md" style={{ borderTop: `1px solid ${borderColor}` }}>
            <Group justify="space-between" align="center" mb="md">
                <Title order={5}>{title}</Title>
                {collapsible && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowJsonView(!showJsonView)}
                    >
                        {showJsonView ? 'Hide' : 'Show'} Before/After
                    </Button>
                )}
            </Group>

            {(showJsonView || !collapsible) && (
                <>
                    {showLegend && (
                        <Box
                            mb="md"
                            p="md"
                            bg="var(--mantine-color-body)"
                            style={{
                                borderRadius: 4,
                                border: `1px solid ${borderColor}`
                            }}
                        >
                            <Text size="sm" fw={700} mb="sm">Color Legend:</Text>
                            <Group gap="xl" wrap="wrap">
                                <Group gap="xs" align="center">
                                    <Box
                                        style={{
                                            width: 16,
                                            height: 16,
                                            backgroundColor: isDark ? '#2e7d32' : '#e8f5e8',
                                            borderRadius: 4,
                                            border: `1px solid ${isDark ? '#4caf50' : '#d4edda'}`
                                        }}
                                    />
                                    <Text size="xs">Added (new properties)</Text>
                                </Group>
                                <Group gap="xs" align="center">
                                    <Box
                                        style={{
                                            width: 16,
                                            height: 16,
                                            backgroundColor: isDark ? '#c62828' : '#ffeaea',
                                            borderRadius: 4,
                                            border: `1px solid ${isDark ? '#ef5350' : '#f5c6cb'}`
                                        }}
                                    />
                                    <Text size="xs">Removed (deleted properties)</Text>
                                </Group>
                                <Group gap="xs" align="center">
                                    <Box
                                        style={{
                                            width: 16,
                                            height: 16,
                                            backgroundColor: isDark ? '#8b6914' : '#fff3cd',
                                            borderRadius: 4,
                                            border: `1px solid ${isDark ? '#b8860b' : '#ffeaa7'}`
                                        }}
                                    />
                                    <Text size="xs">Changed (modified values)</Text>
                                </Group>
                            </Group>
                        </Box>
                    )}

                    <Group gap="md" align="stretch" wrap="nowrap" style={{ overflow: 'hidden' }}>
                        <Box flex={1} miw={0}>
                            <Text size="sm" fw={500} mb={6}>
                                Before (Original)
                            </Text>
                            <Box
                                style={{
                                    border: `1px solid ${borderColor}`,
                                    borderRadius: 4,
                                    overflow: 'auto',
                                    maxHeight: 420
                                }}
                            >
                                <SyntaxHighlighter
                                    language="json"
                                    style={isDark ? monokai : github}
                                    customStyle={{
                                        margin: 0,
                                        padding: 12,
                                        fontSize: 13,
                                        maxHeight: 'none'
                                    }}
                                    codeTagProps={{ style: { fontFamily: 'ui-monospace, monospace' } }}
                                    lineProps={(lineNumber) => getLineProps(lineNumber, false)}
                                    showLineNumbers={true}
                                    wrapLines={true}
                                >
                                    {beforeJson}
                                </SyntaxHighlighter>
                            </Box>
                        </Box>

                        <Box flex={1} miw={0}>
                            <Text size="sm" fw={500} mb={6}>
                                After (Current)
                            </Text>
                            <Box
                                style={{
                                    border: `1px solid ${borderColor}`,
                                    borderRadius: 4,
                                    overflow: 'auto',
                                    maxHeight: 420
                                }}
                            >
                                <SyntaxHighlighter
                                    language="json"
                                    style={isDark ? monokai : github}
                                    customStyle={{
                                        margin: 0,
                                        padding: 12,
                                        fontSize: 13,
                                        maxHeight: 'none'
                                    }}
                                    codeTagProps={{ style: { fontFamily: 'ui-monospace, monospace' } }}
                                    lineProps={(lineNumber) => getLineProps(lineNumber, true)}
                                    showLineNumbers={true}
                                    wrapLines={true}
                                >
                                    {afterJson}
                                </SyntaxHighlighter>
                            </Box>
                        </Box>
                    </Group>
                </>
            )}

        </Box>
    );
};
